import React, { useCallback, useEffect, useState } from 'react';
import { useLessonContext } from '../../LessonContext';
import { getPresignedUrl, courseRunsService } from '../../../../services/courseRunsService';

export function LessonPreview() {
  const { document, lessonRun, refreshRun } = useLessonContext();
  const lessonContent: any = document?.lesson_content;
  const exercises: any = document?.exercises || {};
  const images: any = document?.images || {};
  const audio: any = document?.audio || {};
  const courseContext: any = document?.course_context || {};
  const syllabusEntry: any = courseContext?.syllabus_entry || {};

  const isApproved = (lessonRun?.stages || []).some(
    (s) => s.stage_id === 'lesson_preview' && s.status === 'succeeded'
  );
  const [approving, setApproving] = useState(false);

  const handleApprove = useCallback(async () => {
    if (!lessonRun?.run_id) return;
    setApproving(true);
    try {
      await courseRunsService.approveLessonStage(lessonRun.run_id, 'lesson_preview');
      await refreshRun();
    } finally {
      setApproving(false);
    }
  }, [lessonRun?.run_id, refreshRun]);

  if (!lessonContent) {
    return (
      <div className="rounded border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
        Generate the lesson first to see a preview.
      </div>
    );
  }

  // Track exercise and media indices for matching
  let exerciseIdx = 0;
  let imageIdx = 0;
  let audioIdx = 0;

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-sm font-semibold text-gray-900">Lesson Preview</h3>
          <p className="text-xs text-gray-500">How the learner will experience this lesson</p>
        </div>
        <div className="flex items-center gap-3">
          {isApproved && <span className="text-xs text-green-600">Approved</span>}
          {!isApproved && (
            <button
              type="button"
              className="px-4 py-1.5 text-sm rounded-md text-white bg-green-600 hover:bg-green-700 disabled:opacity-40"
              disabled={approving}
              onClick={handleApprove}
            >{approving ? 'Approving...' : 'Approve for Compile'}</button>
          )}
        </div>
      </div>

      {/* Preview container — styled to approximate learner view */}
      <div className="border border-gray-200 rounded-lg bg-white overflow-hidden">
        {/* Lesson header */}
        <div className="px-8 py-6 border-b border-gray-100" style={{ background: 'linear-gradient(135deg, #1e293b 0%, #334155 100%)' }}>
          <h1 className="text-xl font-bold text-white">{syllabusEntry.title || lessonContent.sections?.[0]?.title || 'Lesson'}</h1>
          {syllabusEntry.subtitle && (
            <p className="text-sm text-slate-300 mt-1">{syllabusEntry.subtitle}</p>
          )}
        </div>

        {/* Lesson body */}
        <div className="px-8 py-6 space-y-6">
          {(lessonContent.sections || []).map((section: any, sIdx: number) => {
            return (
              <div key={section.section_id || sIdx}>
                {section.title && (
                  <h2 className="text-lg font-semibold text-gray-900 mb-3">{section.title}</h2>
                )}
                <div className="space-y-4">
                  {(section.content || []).map((item: any, cIdx: number) => {
                    if (item.type === 'text') {
                      return <TextBlock key={cIdx} body={item.body} />;
                    }
                    if (item.type === 'exercise') {
                      const exId = `ex_${exerciseIdx++}`;
                      const ex = exercises[exId];
                      return <ExerciseBlock key={cIdx} exercise={ex} spec={item} />;
                    }
                    if (item.type === 'image') {
                      const imgId = `img_${imageIdx++}`;
                      const img = images[imgId];
                      return <ImageBlock key={cIdx} image={img} spec={item} />;
                    }
                    if (item.type === 'audio') {
                      const audId = `aud_${audioIdx++}`;
                      const aud = audio[audId];
                      return <AudioBlock key={cIdx} audio={aud} spec={item} />;
                    }
                    if (item.type === 'comparison') {
                      return <ComparisonBlock key={cIdx} item={item} />;
                    }
                    if (item.type === 'callout') {
                      return <CalloutBlock key={cIdx} item={item} />;
                    }
                    return null;
                  })}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

/* ─── Content Blocks ─── */

function TextBlock({ body }: { body: string }) {
  // Simple markdown-ish rendering: bold, italic, line breaks
  const rendered = body
    .split('\n\n')
    .map((para, i) => {
      const html = para
        .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
        .replace(/\*(.+?)\*/g, '<em>$1</em>')
        .replace(/\n/g, '<br/>');
      return <p key={i} className="text-sm text-gray-800 leading-relaxed mb-3" dangerouslySetInnerHTML={{ __html: html }} />;
    });
  return <div>{rendered}</div>;
}

function ExerciseBlock({ exercise, spec }: { exercise: any; spec: any }) {
  const [selectedAnswer, setSelectedAnswer] = useState<number | null>(null);
  const [showFeedback, setShowFeedback] = useState(false);

  if (!exercise) {
    return (
      <div className="bg-green-50 border border-green-200 rounded-lg p-4">
        <div className="text-xs font-bold uppercase text-green-600 mb-1">Exercise (not yet generated)</div>
        <div className="text-sm text-gray-600 italic">{spec.intent}</div>
      </div>
    );
  }

  const answerList = exercise.content_blocks?.find((b: any) => b.type === 'answer_list');
  const questionBlock = exercise.content_blocks?.find((b: any) => b.type === 'text');

  return (
    <div className="bg-green-50 border border-green-200 rounded-lg p-5">
      <div className="text-xs font-bold uppercase text-green-600 mb-3">{exercise.exercise_type || 'Exercise'}</div>
      {questionBlock && (
        <p className="text-sm text-gray-800 mb-4 font-medium">{questionBlock.value}</p>
      )}
      {answerList && (
        <div className="space-y-2">
          {answerList.answers.map((answer: any, i: number) => {
            const isSelected = selectedAnswer === i;
            const isCorrect = answer.type === 'correct';
            let borderClass = 'border-gray-200 bg-white';
            if (showFeedback && isSelected) {
              borderClass = isCorrect ? 'border-green-400 bg-green-50' : 'border-red-300 bg-red-50';
            } else if (isSelected) {
              borderClass = 'border-blue-300 bg-blue-50';
            }
            return (
              <button
                key={i}
                className={`w-full text-left border rounded-md px-4 py-2.5 text-sm transition-colors ${borderClass}`}
                onClick={() => { setSelectedAnswer(i); setShowFeedback(true); }}
              >
                <div className="text-gray-800">{answer.text}</div>
                {showFeedback && isSelected && answer.feedback && (
                  <div className={`text-xs mt-2 ${isCorrect ? 'text-green-700' : 'text-red-700'}`}>
                    {answer.feedback}
                  </div>
                )}
              </button>
            );
          })}
        </div>
      )}
      {exercise.exercise_type === 'free-form' && (
        <textarea
          className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm mt-2 resize-y"
          rows={4}
          placeholder="Write your response here..."
          disabled
        />
      )}
    </div>
  );
}

function ImageBlock({ image, spec }: { image: any; spec: any }) {
  const [url, setUrl] = useState<string | null>(null);

  useEffect(() => {
    if (image?.s3_keys?.[0]) {
      getPresignedUrl(image.s3_keys[0]).then(setUrl).catch(() => {});
    }
  }, [image?.s3_keys]);

  return (
    <div className="rounded-lg overflow-hidden">
      {url ? (
        <img src={url} alt={spec.alt_text || spec.description} className="w-full rounded-lg" />
      ) : image?.s3_keys?.[0] ? (
        <div className="bg-gray-100 rounded-lg p-8 text-center text-sm text-gray-400">Loading image...</div>
      ) : (
        <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
          <div className="text-xs font-bold uppercase text-purple-600 mb-1">Image (not yet generated)</div>
          <div className="text-sm text-gray-600 italic">{spec.description}</div>
        </div>
      )}
    </div>
  );
}

function AudioBlock({ audio, spec }: { audio: any; spec: any }) {
  const [url, setUrl] = useState<string | null>(null);

  useEffect(() => {
    if (audio?.s3_key) {
      getPresignedUrl(audio.s3_key).then(setUrl).catch(() => {});
    }
  }, [audio?.s3_key]);

  return (
    <div className="bg-pink-50 border border-pink-200 rounded-lg p-4">
      <div className="text-xs font-bold uppercase text-pink-600 mb-2">Audio</div>
      {spec.description && <div className="text-sm text-gray-600 mb-2">{spec.description}</div>}
      {url ? (
        <audio controls className="w-full" preload="metadata">
          <source src={url} type="audio/mpeg" />
        </audio>
      ) : audio?.s3_key ? (
        <div className="text-xs text-gray-400">Loading audio...</div>
      ) : (
        <div className="text-sm text-gray-500 italic">Audio not yet generated</div>
      )}
      {(audio?.transcript || spec.transcript) && (
        <details className="mt-2">
          <summary className="text-xs text-gray-500 cursor-pointer">Show transcript</summary>
          <div className="text-xs text-gray-600 mt-1 bg-white rounded p-2">{audio?.transcript || spec.transcript}</div>
        </details>
      )}
    </div>
  );
}

function ComparisonBlock({ item }: { item: any }) {
  return (
    <div className="grid grid-cols-2 gap-3">
      {(item.items || []).map((comp: any, i: number) => (
        <div key={i} className={`border rounded-lg p-4 ${
          i === 0 ? 'border-red-200 bg-red-50' : 'border-green-200 bg-green-50'
        }`}>
          <div className={`text-xs font-bold uppercase mb-2 ${
            i === 0 ? 'text-red-600' : 'text-green-600'
          }`}>{comp.label}</div>
          <div className="text-sm text-gray-800">{comp.content}</div>
        </div>
      ))}
    </div>
  );
}

function CalloutBlock({ item }: { item: any }) {
  return (
    <div className="bg-cyan-50 border-l-4 border-cyan-400 rounded-r-lg px-4 py-3">
      <div className="text-sm text-gray-800">{item.body}</div>
    </div>
  );
}
